import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getErrorMessage } from '../../lib/errors';
import { AuthCard } from './AuthCard';
import { AuthAlert, AuthField, AuthSubmit, PasswordRules } from './AuthField';
import { useAuth } from './useAuth';

/**
 * Two modes on one route, both delegated to Supabase:
 *  - no session: request a reset email
 *  - session present (arrived via the recovery link, which supabase-js exchanges
 *    for a session automatically): set a new password
 */
export function PasswordResetPage() {
  const { session, isLoading, requestPasswordReset, updatePassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);

  async function handleRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await requestPasswordReset(email);
      setIsSent(true);
    } catch (caught) {
      setError(getErrorMessage(caught, 'Could not send the reset email. Try again.'));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleUpdate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await updatePassword(password);
      void navigate('/', { replace: true });
    } catch (caught) {
      setError(getErrorMessage(caught, 'Could not update the password. Try again.'));
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading) {
    return <AuthCard title="Reset your password" description="Checking your link." />;
  }

  if (session) {
    return (
      <AuthCard
        title="Set a new password"
        description="Choose a password you don’t use anywhere else."
      >
        <form className="auth-form" onSubmit={(event) => void handleUpdate(event)} noValidate>
          <AuthField
            label="New password"
            type="password"
            name="password"
            placeholder="At least 8 characters"
            autoComplete="new-password"
            minLength={8}
            required
            hint={<PasswordRules password={password} />}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />

          {error ? <AuthAlert message={error} /> : null}

          <AuthSubmit label="Save password" isBusy={isSubmitting} />
        </form>
      </AuthCard>
    );
  }

  if (isSent) {
    return (
      <AuthCard
        icon="mail"
        title="Check your email"
        description={`If an account exists for ${email.trim() || 'that address'}, a reset link is on its way. Open it to set a new password.`}
        footer={
          <>
            Wrong address?{' '}
            <button className="link-button" type="button" onClick={() => setIsSent(false)}>
              Try another email
            </button>
          </>
        }
      />
    );
  }

  return (
    <AuthCard
      title="Reset your password"
      description="Enter the email you sign in with and we’ll send you a link to set a new password."
      footer={
        <>
          Remembered it? <Link to="/login">Back to sign in</Link>
        </>
      }
    >
      <form className="auth-form" onSubmit={(event) => void handleRequest(event)} noValidate>
        <AuthField
          label="Email"
          type="email"
          name="email"
          placeholder="you@company.com"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        {error ? <AuthAlert message={error} /> : null}

        <AuthSubmit label="Send reset link" isBusy={isSubmitting} />
      </form>
    </AuthCard>
  );
}
